import React, { useEffect, useState } from 'react';
import { Clock, Users, Heart, ChefHat, Utensils, createLucideIcon } from 'lucide-react';
import { cuisineFlags, cuisineIcons, tagColor } from '../../lib/tagsManager';
import { isSafeInteger } from 'lodash';
import { mortarPestle, pepperChilli } from '@lucide/lab';
import { cuisineText } from '@/lib/tagManager';
// import { motion, AnimatePresence } from 'framer-motion';


export interface Recipe {
  id: string;
  title: string;
  image: string;
  prepTime: string;
  cookTime: string;
  servings: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  category: string[];
  isFavorite: boolean;
  ingredients?: string[];
  instructions?: string[];
  notes?: string;
  calories?: number | string;
  tags?: string[];
  spiceLevel?: string;
  thumbUrl?: string;
}

function decodeHtmlEntities(str: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");
  return doc.documentElement.textContent || '';
}

interface RecipeCardProps {
  recipe: Recipe;
  onToggleFavorite: (id: string) => void;
  i?: number,
  onClick: (recipe: Recipe) => void;
}

const RecipeCard: React.FC<RecipeCardProps> = ({ recipe, onToggleFavorite, onClick, i }) => {
  const { id, title, image, thumbUrl, prepTime, cookTime, servings, difficulty, category, isFavorite, tags, spiceLevel } = recipe;

  const [favoriteToggled, setFavoriteToggled] = useState(isFavorite);

  const handleToggle = (id: any, isFavorite: boolean) => {
    setFavoriteToggled(!isFavorite);
    onToggleFavorite(id);
  }

  const totalTime = () => {
    const prep = parseInt(prepTime);
    const cook = parseInt(cookTime);
    return prep + cook;
  };

  const PepperChilli = createLucideIcon("PepperChilli", pepperChilli);

  const difficultyColor = (diff?: string) => {
    let _difficulty = String(difficulty);
    if (diff) _difficulty = diff;
    switch (_difficulty) {
      case 'Easy': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      case 'Hard': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'Cuisine': return 'bg-purple-100 text-pink-800 dark:bg-blue/30 dark:text-white';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const mealTypes = ["Breakfast", "Lunch", "Dinner"];

  let meals = [];
  let cuisines = [];
  let cleanTags = [];
  let abbrev: Record<string, string> = {
    "Vegetarian": "V",
    "Vegan": "VE",
    "Dairy Free": "DF",
    "Gluten Free": "GF",
    "Nut Free": "NF",
    "Sugar Free": "SF",
    "Low Calorie": "LC",
    "High Protein": "HP",
    "Organic": "O",
    "Pescatarian": "P",
    "Halal": "H",
    "Kosher": "K",
    "Soy Free": "SFY",
    "Egg Free": "EF",
    "Low FODMAP": "LF",
  };

  for (const item of category) {
    const cleanCategory = decodeHtmlEntities(item);
    if (mealTypes.includes(item)) {
      meals.push(cleanCategory);
    } else {
      cuisines.push(cleanCategory);
    }
  }

  if (tags) {
    for (const item of tags) {
      const cleanTag = decodeHtmlEntities(item);
      cleanTags.push(cleanTag);
    }
  }

  // const [index, setIndex] = useState(0);

  // useEffect(() => {
  //   const timer = setInterval(() => {
  //     setIndex((prev) => (prev + 1) % icons.length);
  //   }, 1200);
  //   return () => clearInterval(timer);
  // }, []);

  const hoverColor = `from-gray-900/80`;
  // const hoverColor = i == 0 ? `from-gray-900/80`: i == 1 ? `from-orange-400/80` : i == 2 ? `from-secondary/80`: `from-gray-400/80`;

  const mealType = meals.length > 0 ? meals[0] : "Meal";

  return (
    <div
      className={`group relative cursor-pointer hover:border-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border ${mealType.toLowerCase() == "breakfast" ? "border-[#FFD447]" : mealType.toLowerCase() == "lunch" ? "border-[#FF5E5B]" : "border-[#A259FF]"} transition-all duration-300 hover:shadow-md`}
      data-tooltip="view-recipe"
      onClick={() => onClick(recipe)}
    >
      <div className="relative">
        <img
          src={thumbUrl ? thumbUrl.replace("w_150", "w_300").replace("h_150", "h_300") : image}
          alt={title}
          className="w-full h-48 object-cover"
        />
        <button
          data-tooltip="favorites"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle(id, favoriteToggled);
          }}
          className={`absolute top-3 right-3 p-2 rounded-full ${favoriteToggled
            ? 'bg-red-600 text-white'
            : 'bg-white/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
            } transition-colors duration-200`}
        >
          <Heart size={18} fill={favoriteToggled ? 'white' : 'none'} />
        </button>
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex flex-wrap gap-2 max-w-xs">
            <span className={`text-xs flex font-medium px-2 py-1 rounded-full ${difficultyColor()}`}>
              {difficulty}
            </span>
            {cuisines.slice(0, 1).map((cuisine, index) => (
              <span
                key={index}
                className={`text-xs flex items-center px-2 py-1 rounded-full ${difficultyColor("Cuisine")}`}
              >
                {/* {cuisineFlags[cuisine] || cuisineFlags.Default} */}
                {/* &nbsp; */}
                {cuisine}
              </span>
            ))}
            {cleanTags?.slice(0, 3).map((tag, index) => {
              if (cuisineText[tag]) return (
                <span
                  key={index}
                  className={`text-xs flex items-center rounded-full text-gray-800 dark:text-gray-200`}
                >
                  {cuisineText[tag]}
                  {/* {abbrev[tag]} */}
                </span>
              )
            })}
          </div>
          {/* Hover Swipe Overlay (NEW) */}
          {/* <div className={`absolute inset-0 bg-gradient-to-l ${hoverColor} to-transparent text-white text-sm font-medium flex flex-col justify-center px-6 transform translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-in-out z-10 pointer-events-none space-y-2`}>
            <div className="flex flex-wrap gap-2 max-w-xs">
              {cuisines.slice(0, 6).map((cuisine, index) => (
                <span
                  key={index}
                  className={`text-xs flex items-center px-2 py-1 rounded-full ${tagColor(cuisine)} text-gray-800 dark:text-gray-200`}
                >
                  {cuisineIcons[cuisine] || cuisineIcons.Default}
                  {cuisine}
                </span>
              ))}

              {cleanTags?.slice(0, 6).map((tag, index) => (
                <span
                  key={index}
                  className={`text-xs flex items-center px-2 py-1 rounded-full ${tagColor(tag)} text-gray-800 dark:text-gray-200`}
                >
                  {cuisineIcons[tag] || cuisineIcons.Default}
                  {abbrev[tag]}
                </span>
              ))}
            </div>
          </div> */}
          {/* {Array.isArray(meals) && meals.map((each, index) => <span key={index} className="ml-2 text-xs font-medium px-2 py-1 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-800 dark:text-gray-200">
            {each}
          </span>)} */}
        </div>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-lg text-gray-800 hover:underline dark:text-white mb-2 sm:overflow-hidden sm:text-ellipsis sm:whitespace-nowrap">{title}</h3>

        <div className="flex items-center text-gray-600 dark:text-gray-400 text-sm mb-4">
          {/* <Utensils size={16} className="mr-1" />
          <span className="mr-1">{meals[0]}</span> */}
          <Clock size={16} className="mx-1" />
          <span>{isNaN(totalTime()) ? cookTime : `${totalTime()} mins`}</span>

          <span className="mx-2">•</span>

          {spiceLevel && <><PepperChilli size={16} className="mx-1" />
            <span>{spiceLevel}</span></>}

          <span className="mx-2">•</span>

          <Users size={16} className="mr-1" />
          <span>{servings} servings</span>
        </div>

        {/* <button
          data-tooltip="view-recipe"
          onClick={() => onClick(recipe)}
          className="w-full py-2 px-4 bg-secondary hover:bg-secondary/90 text-white rounded-lg transition-colors duration-200"
        >
          View Recipe
        </button>
        <button
          onClick={() => onClick(recipe)}
          className="relative overflow-hidden w-full py-2 px-4 bg-secondary text-white rounded-lg group transition-all duration-300"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            <Eye size={16} />
            View Recipe
          </span>
          <span className="absolute inset-0 bg-white/10 group-hover:translate-x-full transition-transform duration-500 ease-out">Yo</span>
        </button>
        <button
          onClick={() => onClick(recipe)}
          className="w-full py-2 px-4 bg-secondary text-white rounded-lg transition-all duration-200 hover:brightness-110 active:scale-95"
        >
          View Recipe
        </button> */}
        {/* <button
          data-tooltip="view-recipe"
          onClick={() => onClick(recipe)}
          className="relative w-full py-2 px-4 bg-black text-white rounded-lg overflow-hidden transition-all cursor-pointer duration-300 hover:scale-105 hover:border-[#ffffff] hover:shadow-lg group"
        >
          <span className="relative z-10 flex items-center justify-center gap-2">

            <ChefHat className="text-white" size={20} /> View Recipe
          </span>
        </button> */}
        <button
          data-tooltip="view-recipe"
          onClick={() => onClick(recipe)}
          className="relative w-full py-1.5 px-2 bg-black btn-sm text-sm text-white rounded-lg overflow-hidden transition-all cursor-pointer duration-300 hover:scale-105 hover:border-[#ffffff] hover:shadow-lg group"
        >
          <span className="inline-flex items-center gap-2">
            <ChefHat className="text-white" size={14} />
            View Recipe
          </span>
        </button>
      </div>
    </div >
  );
};

export default RecipeCard;